import React, { ChangeEvent, useCallback } from 'react';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Badge,
  CircularProgress,
  Fade,
  IconButton,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import AlertIcon from '@material-ui/icons/Notifications';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import EquipmentDetails from './EquipmentDetails';
import EquipmentTagList from './EquipmentTagList';
// import EquipmentFirmware from './Firmware/EquipmentFirmware';
import { AlarmStatus } from '../../../models';
import { EquipmentTabProps } from './EquipmentPage.d';
import { PathBuilder } from '../../../routing';
import identifiers from '../../../tests/identifiers';
import { SubscriptionLifecycle, SubscriptionStatus } from '../../../state/subscription';
import { TabButton, TabHeader, TabPanel, TabsContainer } from 'src/components/ui/Tabs';

const EquipmentTabPanel = (props: EquipmentTabProps) => {
  // TODO: Remove this once the tabs are implemented
  const { activeTab, equipment, equipmentAlarms, pinnedTagIds, siteId, tagDataStatus } = props;
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('sm'));

  // TODO: Remove this once the tabs are implemented
  const [value, setValue] = useState(getTabIndex(activeTab));
  const { equipmentType, id, label } = equipment;

  const relevantAlarms = useMemo(() => {
    const tagIds = equipment.tags.map((tag) => tag.id);
    return equipmentAlarms.filter(
      ({ alarmTag, status }) =>
        (tagIds.includes(alarmTag.id) && status === AlarmStatus.ACTIVE_UN_ACK) ||
        status === AlarmStatus.ACTIVE_ACK
    );
  }, [equipment, equipmentAlarms]);

  const handleTabChange = useCallback(
    (_: ChangeEvent<{}>, newValue?: number) => {
      if (newValue !== undefined) {
        setValue(newValue);
        history.push(PathBuilder.EQUIPMENT(siteId, pinnedTagIds, id, EQUIPMENT_TABS[newValue].id));
      }
    },
    [history, id, pinnedTagIds, siteId]
  );

  return (
    <Fade in>
      <div
        className={classes.root}
        data-testid={identifiers.equipmentPage.tabPanel}
        role="tabpanel"
        id={`equipment-tabpanel-${id}`}
        aria-labelledby={`equipment-tab-${id}`}
      >
        <div className={classes.actions}>
          <Fade in={relevantAlarms.length > 0}>
            <Tooltip
              title={`${relevantAlarms.length} Active Alarm${
                relevantAlarms.length === 1 ? '' : 's'
              }`}
            >
              <Badge
                badgeContent={relevantAlarms.length}
                classes={{ root: classes.alertBadgeRoot, badge: classes.alertBadge }}
                color="secondary"
              >
                <IconButton onClick={() => history.push(PathBuilder.ALARMS_DEFAULT(siteId))}>
                  <AlertIcon />
                </IconButton>
              </Badge>
            </Tooltip>
          </Fade>
          <Tooltip title="Close">
            <IconButton
              data-testid={identifiers.equipmentPage.closeEquipment}
              onClick={() => history.push(PathBuilder.EQUIPMENT(siteId, pinnedTagIds))}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
        <Typography color="textSecondary" variant="subtitle1">
          {equipmentType}
        </Typography>
        <Typography variant={isSmallScreen ? 'h5' : 'h4'} gutterBottom>
          {label}
          <TagDataStatus tagDataStatus={tagDataStatus} />
        </Typography>

        <TabsContainer>
          <TabHeader value={value} onChange={handleTabChange}>
            {EQUIPMENT_TABS.map((tab) => (
              <TabButton key={tab.id} label={tab.label} />
            ))}
          </TabHeader>
          {EQUIPMENT_TABS.map(({ id: tabId, Component }, index) => {
            return (
              <TabPanel value={value} index={index}>
                <Component key={tabId} {...props} />
              </TabPanel>
            );
          })}
        </TabsContainer>
      </div>
    </Fade>
  );
};

const EQUIPMENT_TABS = [
  { Component: EquipmentTagList, id: 'tags', label: 'Tag Points' },
  { Component: EquipmentDetails, id: 'details', label: 'Details' },
  // {
  //  Component: EquipmentFirmware,
  //  id: 'firmware',
  //  label: 'Firmware',
  // },
];

function getTabIndex(activeTab: string) {
  return EQUIPMENT_TABS.findIndex(({ id }) => id === activeTab) || 0;
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    overflow: 'scroll',
    padding: theme.spacing(3),
    flexGrow: 1,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  actions: {
    float: 'right',
    marginTop: theme.spacing(2),
  },
  alertBadge: {
    right: 4,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: theme.spacing(0, 0.5),
  },
  alertBadgeRoot: {
    marginRight: theme.spacing(1),
  },
  tabs: {
    marginBottom: theme.spacing(3),
  },
}));

const TagDataStatus = ({
  tagDataStatus = SubscriptionLifecycle.STARTING,
}: {
  tagDataStatus: SubscriptionStatus;
}) => {
  const classes = useStatusStyles();
  switch (tagDataStatus) {
    case SubscriptionLifecycle.STARTING:
      return (
        <CircularProgress
          className={classes.loader}
          data-testid={identifiers.equipmentPage.tagDataLoading}
          size={24}
        />
      );
    case SubscriptionLifecycle.ACTIVE:
      return null;
    case SubscriptionLifecycle.STOPPED:
    default:
      return (
        <Tooltip placement="top" title="Error Retrieving Tag Point Data">
          <ErrorIcon
            className={classes.errorIcon}
            data-testid={identifiers.equipmentPage.tagDataError}
          />
        </Tooltip>
      );
  }
};

const useStatusStyles = makeStyles<Theme>((theme) => ({
  errorIcon: {
    color: theme.palette.error.main,
    margin: theme.spacing(0, 0, -0.5, 1),
  },
  loader: {
    margin: theme.spacing(0, 0, -0.5, 1),
  },
}));

export default React.memo(EquipmentTabPanel);
